import {
  Users,
  FileText,
  MessageSquare,
  Megaphone,
  Tags,
  Package,
  LogOut,
  MoreVertical,
  ShieldCheck,
  type LucideIcon
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { getInitials } from "@/utils/formatters";
import { authActions } from "@/store/reducers/auth.reducer";
import { cn } from "@/utils/shadcn";

type NavItemData = {
  label: string;
  path: string;
  icon: LucideIcon;
};

const navItems: NavItemData[] = [
  { label: "Organizations", path: "/organizations", icon: Users },
  { label: "Packages", path: "/packages", icon: Package },
  { label: "Pricing", path: "/pricing", icon: Tags },
  { label: "Logs", path: "/logs", icon: FileText },
  { label: "Feedback", path: "/feedback", icon: MessageSquare },
  { label: "System Messages", path: "/system-messages", icon: Megaphone }
];

const NAV_LABEL_CLASS =
  "text-[10.5px] font-semibold text-muted-foreground/80 uppercase tracking-[0.09em] px-2.5 pt-4 pb-1 h-auto";

const navButtonClass = (isActive: boolean) =>
  cn(
    "relative h-[38px] px-2.5 rounded-[9px] text-[13.5px] font-medium",
    "text-sidebar-foreground hover:bg-sidebar-accent",
    "data-[active=true]:bg-primary/10 data-[active=true]:text-primary data-[active=true]:hover:bg-primary/10",
    isActive && "bg-primary/10 text-primary hover:bg-primary/10 hover:text-primary"
  );

const ActiveBar = () => (
  <span className="absolute left-0 top-2 bottom-2 w-[3px] rounded-r-[3px] bg-primary" />
);

function NavItem({ item, isActive }: { item: NavItemData; isActive: boolean }) {
  const Icon = item.icon;
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.label}
        className={navButtonClass(isActive)}
      >
        <Link to={item.path} className="flex items-center gap-[11px] w-full">
          {isActive && <ActiveBar />}
          <span className="w-[18px] flex justify-center shrink-0">
            <Icon className="w-[17px] h-[17px]" />
          </span>
          <span className="flex-1 text-left">{item.label}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AppSidebar() {
  const location = useLocation();
  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();

  const isPathActive = (path: string): boolean =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleLogout = () => {
    dispatch(authActions.signOut());
  };

  return (
    <Sidebar side="left" className="border-sidebar-border bg-sidebar z-50">
      <SidebarHeader className="p-3 border-b border-sidebar-border bg-sidebar">
        <div className="flex items-center gap-2.5 px-1 py-1">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-primary text-primary-foreground shadow-sm">
            <ShieldCheck className="h-[18px] w-[18px]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[13.5px] font-semibold text-sidebar-foreground leading-tight truncate">
              Enigma Admin
            </p>
            <p className="text-[11.5px] text-muted-foreground leading-tight truncate">
              Control panel
            </p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="px-2.5 py-3">
        <SidebarGroup>
          <SidebarGroupLabel className={cn(NAV_LABEL_CLASS, "pt-2")}>Manage</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-0.5">
              {navItems.map((item) => (
                <NavItem key={item.path} item={item} isActive={isPathActive(item.path)} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-2.5 border-t border-sidebar-border bg-sidebar">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2.5 w-full p-2 rounded-[10px] hover:bg-sidebar-accent transition-colors text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
              <Avatar className="h-[34px] w-[34px]">
                <AvatarFallback className="bg-primary/[0.12] text-primary text-[13px] font-semibold">
                  {getInitials(user?.name ?? "")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0 text-left">
                <p className="text-[13px] font-semibold text-sidebar-foreground truncate">
                  {user?.name ?? "Admin"}
                </p>
                <p className="text-[11.5px] text-muted-foreground truncate">{user?.email}</p>
              </div>
              <MoreVertical className="w-[15px] h-[15px] text-muted-foreground shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[240px] rounded-xl p-1.5">
            <div className="flex items-center gap-2.5 px-2 pt-2 pb-2.5 mb-1 border-b border-border">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/[0.12] text-primary text-[13px] font-semibold">
                  {getInitials(user?.name ?? "")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold text-foreground truncate">
                  {user?.name ?? "Admin"}
                </p>
                <p className="text-[11.5px] text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <DropdownMenuSeparator className="mx-1 my-1" />
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex items-center gap-2.5 rounded-[9px] px-2.5 py-2 text-[13px] font-semibold text-destructive focus:text-destructive focus:bg-destructive/10"
            >
              <LogOut className="w-[15px] h-[15px] shrink-0" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
